/**
 * Pan and zoom of one photo in the full-screen viewer (`PhotoViewer.tsx`):
 * the same pure `{scale, x, y}` transform as the terrain map's own
 * `mapView.ts`, generalised to a rectangular box instead of a square one,
 * since a photo's own box is the viewport, not a fixed world size. Kept pure
 * and apart from the component so the maths are proved by a unit test
 * (photoView.test.ts) and the viewer stays a single CSS transform on an
 * `<img>`, nothing more.
 */
export interface PhotoView {
  /** 1 shows the photo at the box's own size; MAX_SCALE is as close as it goes. */
  scale: number;
  /** Where the scaled photo's top left corner sits, in box pixels. */
  x: number;
  y: number;
}

/** The photo at rest, which is where the viewer opens on every capture. */
export const FIT_VIEW: PhotoView = { scale: 1, x: 0, y: 0 };
export const MIN_SCALE = 1;
export const MAX_SCALE = 8;
/** One press of the + or − button, or one notch of the wheel. */
export const STEP = 1.4;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/**
 * Zoom within range and no empty margin at either edge, whatever the pan
 * asked for. At 1x (or less) the photo is never panned off its own box: `x`
 * and `y` snap back to 0, so the fitted photo always sits centred rather
 * than drifting from an earlier drag.
 */
export function clampView(view: PhotoView, width: number, height: number): PhotoView {
  const scale = clamp(view.scale, MIN_SCALE, MAX_SCALE);
  if (scale <= 1) return { scale, x: 0, y: 0 };
  const minX = width - width * scale;
  const minY = height - height * scale;
  return { scale, x: clamp(view.x, minX, 0), y: clamp(view.y, minY, 0) };
}

/** Zoom by `factor` leaving whatever is under (ox, oy), in box pixels, where it is. */
export function zoomAt(
  view: PhotoView,
  factor: number,
  ox: number,
  oy: number,
  width: number,
  height: number,
): PhotoView {
  const scale = clamp(view.scale * factor, MIN_SCALE, MAX_SCALE);
  const k = scale / view.scale;
  return clampView({ scale, x: ox - (ox - view.x) * k, y: oy - (oy - view.y) * k }, width, height);
}

/** Pan by a drag of (dx, dy) box pixels. */
export const panBy = (
  view: PhotoView,
  dx: number,
  dy: number,
  width: number,
  height: number,
): PhotoView => clampView({ scale: view.scale, x: view.x + dx, y: view.y + dy }, width, height);
